/**
 * Broker Bancario (ticket #1226): piezas puras de su lista de clientes por
 * contactar. Sin dependencias de Supabase, para probarlas solas.
 */
import { estadoFormaPago } from "@/lib/portal-cobranza/accionesFinanciamiento";
import { STATUS_DESCRIPTORS, type LeadStatus } from "@/lib/portal-bancos/bank-leads";

/**
 * Enlace de WhatsApp: lada + número en dígitos. Si el número ya trae la lada
 * (más de 10 dígitos y empieza con ella) no se repite. Sin teléfono, null.
 */
export function enlaceWhatsApp(lada: string | null, telefono: string | null): string | null {
  const num = (telefono ?? "").replace(/\D/g, "");
  if (!num) return null;
  const pre = (lada ?? "").replace(/\D/g, "");
  const completo = pre && !(num.length > 10 && num.startsWith(pre)) ? pre + num : num;
  return `https://wa.me/${completo}`;
}

/**
 * Filtro "Forma de pago" de los clientes por contactar (ticket #1245): "" = todos,
 * `sin_definir` = aún no elige, `FINANCIAMIENTO_EXTERNO` = eligió otro banco u otro
 * arreglo y pidió que lo contacten, `CREDITO_HIPOTECARIO` = eligió crédito y le falta
 * su solicitud (migración 20261008071953). Mismos valores que `estadoFormaPago`.
 */
export type FiltroFormaPagoBroker = "" | "sin_definir" | "FINANCIAMIENTO_EXTERNO" | "CREDITO_HIPOTECARIO" | "RECURSOS_PROPIOS";

// ── Eligió crédito y le falta su solicitud (ticket #1245) ─────────────────────
// La supervisora lo pasa a crédito (broker_cambiar_forma_pago) o él lo eligió en la
// app, y todavía no manda su solicitud: la completa en su app (monto, plazo y su
// autorización para compartir sus datos). Clientes por contactar lo trae para darle
// seguimiento.

/** Etiqueta del caso en la lista y en la ficha. */
export const ETIQUETA_CREDITO_SIN_SOLICITUD = "Eligió crédito, falta su solicitud";
/** Explicación del caso (tooltip). */
export const AYUDA_CREDITO_SIN_SOLICITUD =
  "La forma de pago se cambió a crédito; el cliente debe completar su solicitud en su app";

/** Cierres sin crédito: no cuentan como solicitud (le falta volver a mandarla). */
const CIERRES_SIN_CREDITO = ["rechazado", "desistido", "expirada"];

/**
 * ¿Eligió crédito y le falta su solicitud? En una fila de Clientes por contactar basta
 * la forma de pago: la RPC solo trae crédito cuando no hay solicitud. En la ficha se
 * pasan sus solicitudes: cualquiera que no sea un cierre sin crédito (viva, formalizada
 * o atendida por fuera) ya lleva el crédito. Misma regla que la RPC.
 */
export function faltaSolicitudCredito(
  tipo: string | null | undefined,
  solicitudes?: { estatus: string }[] | null,
): boolean {
  if (estadoFormaPago(tipo) !== "CREDITO_HIPOTECARIO") return false;
  return !(solicitudes ?? []).some((s) => !CIERRES_SIN_CREDITO.includes(s.estatus));
}

/** Lo que se necesita de una fila del broker para saber dónde va con su pago final. */
interface FormaPagoCliente {
  /** null o ausente (RPC anterior a la migración) = aún no elige. */
  tipo_financiamiento?: string | null;
  /** La app ya le muestra la elección de forma de pago final. */
  pago_final_disponible: boolean;
}

/** ¿Eligió financiamiento externo? */
export function eligioFinanciamientoExterno(tipo: string | null | undefined): boolean {
  return estadoFormaPago(tipo) === "FINANCIAMIENTO_EXTERNO";
}

/**
 * Aún no elige y su app ya le muestra la elección: el KPI "Ya pueden elegir" y el
 * switch "Solo los que ya pueden elegir". Quien eligió financiamiento externo ya
 * eligió, aunque la app se lo siga mostrando.
 */
export function yaPuedeElegir(f: FormaPagoCliente): boolean {
  return f.pago_final_disponible && estadoFormaPago(f.tipo_financiamiento) === "sin_definir";
}

/**
 * Pestañas de estatus de la lista Clientes del broker: la forma de pago o "conversacion" (ya hay
 * chat con el cliente). Quien liquida con recursos propios solo sale en su pestaña: el broker no
 * lo persigue, pero lo ve por si quiere cambiar de forma de pago (migración 20261013214838).
 */
export type TabClientesBroker = FiltroFormaPagoBroker | "conversacion";

/**
 * Crédito externo y recursos propios no los trabaja el broker: solo el Super Admin ve esas
 * pestañas (y esas filas), para hacer desde ahí un cambio de forma de pago si lo piden.
 */
export function soloSuperAdminEnClientes(tipo: string | null | undefined): boolean {
  const estado = estadoFormaPago(tipo);
  return estado === "FINANCIAMIENTO_EXTERNO" || estado === "RECURSOS_PROPIOS";
}

/**
 * Solicitudes que el broker ya no lleva: atendidas por fuera, o de una cuenta que pasó a crédito
 * externo o recursos propios (se cerraron al cambiar la forma de pago). Las sigue viendo el Super
 * Admin en "Todos los bancos"; las revisa Escrituración.
 */
export function solicitudFueraDelBroker(l: { status: string; formaPagoCuenta?: string | null }): boolean {
  return l.status === "atendido_fuera" || soloSuperAdminEnClientes(l.formaPagoCuenta);
}

/**
 * ¿La fila va en la pestaña? `conChat`: la cuenta ya tiene mensajes en el chat del broker.
 * `completo`: Super Admin, que también ve crédito externo y recursos propios.
 */
export function coincideTabClientes(
  tipo: string | null | undefined,
  tab: TabClientesBroker,
  conChat: boolean,
  completo = true,
): boolean {
  if (!completo && soloSuperAdminEnClientes(tipo)) return false;
  const recursosPropios = estadoFormaPago(tipo) === "RECURSOS_PROPIOS";
  if (tab === "RECURSOS_PROPIOS") return recursosPropios;
  if (recursosPropios) return false;
  if (tab === "conversacion") return conChat;
  return coincideFormaPago(tipo, tab);
}

/** ¿La fila pasa el filtro "Forma de pago"? */
export function coincideFormaPago(tipo: string | null | undefined, filtro: FiltroFormaPagoBroker): boolean {
  return !filtro || estadoFormaPago(tipo) === filtro;
}

/**
 * "En la app" de la ficha del cliente del broker: si entra, si ya eligió y qué.
 * `falta_solicitud`: eligió crédito y no tiene solicitud (`faltaSolicitudCredito`).
 */
export function estadoEnAppFicha(
  f: FormaPagoCliente & { tiene_usuario_app: boolean; falta_solicitud?: boolean },
): string {
  if (!f.tiene_usuario_app) return "Sin usuario";
  if (f.falta_solicitud && estadoFormaPago(f.tipo_financiamiento) === "CREDITO_HIPOTECARIO") {
    return ETIQUETA_CREDITO_SIN_SOLICITUD;
  }
  if (eligioFinanciamientoExterno(f.tipo_financiamiento)) {
    return "Ya tiene crédito hipotecario con otro banco";
  }
  if (estadoFormaPago(f.tipo_financiamiento) !== "sin_definir") return "Ya eligió su pago final";
  return f.pago_final_disponible ? "Ya puede elegir su pago final" : "Aún no ve el pago final";
}

/** 'pf' / 'pm' en `personas.tipo_persona` (tolera 'FISICA' / 'MORAL'). */
export function etiquetaTipoPersona(tipo: string | null | undefined): string | null {
  const t = (tipo ?? "").trim().toLowerCase();
  if (t === "pf" || t.startsWith("fis") || t.startsWith("fís")) return "Persona física";
  if (t === "pm" || t.startsWith("mor")) return "Persona moral";
  return null;
}

const BASE_FICHAS = "/admin/portal-bancos/clientes/";

/** Ruta de la ficha del cliente del broker (por su cuenta de cobranza). */
export function rutaFichaCliente(idCuenta: number): string {
  return `${BASE_FICHAS}${idCuenta}`;
}

/** Copropietarios que la RPC manda como "Nombre A, Nombre B": uno por renglón. */
export function listaCopropietarios(texto: string | null | undefined): string[] {
  return (texto ?? "").split(", ").map((n) => n.trim()).filter(Boolean);
}

/**
 * Vista completa del broker con una solicitud elegida (si el cliente tiene varias).
 * `desde` es la sección del portal donde se abrió ("solicitudes", …): el menú la marca
 * a ella y no a "Clientes por contactar", de cuya ruta cuelga la vista.
 */
export function rutaVistaSolicitud(idCuenta: number, idSolicitud: string | number, desde?: string | null): string {
  const q = new URLSearchParams({ solicitud: String(idSolicitud) });
  if (desde) q.set("desde", desde);
  return `${rutaFichaCliente(idCuenta)}?${q.toString()}`;
}

const PREFIJO_PORTAL_BANCOS = "/admin/portal-bancos/";

/**
 * Secciones renombradas (la ruta del submenú lleva el nombre del menú). Un enlace
 * guardado con `?desde=` viejo sigue marcando la opción correcta del menú.
 */
const SECCIONES_RENOMBRADAS: Readonly<Record<string, string>> = {
  bandeja: "solicitudes",
  broker: "clientes",
  equipo: "ejecutivos",
  bancos: "convenios",
};
const SECCION_VALIDA = /^[a-z0-9-]+$/;

/** Sección del Portal Bancos de una ruta ("/admin/portal-bancos/solicitudes" → "solicitudes"). */
export function seccionDeRuta(pathname: string): string | null {
  if (!pathname.startsWith(PREFIJO_PORTAL_BANCOS)) return null;
  const seccion = pathname.slice(PREFIJO_PORTAL_BANCOS.length).split("/")[0];
  return SECCION_VALIDA.test(seccion) ? seccion : null;
}

/**
 * Ruta con la que el menú del portal decide qué opción marcar y qué título poner.
 * La vista del broker cuelga de su sección; si se abrió desde otra (`?desde=`), es
 * esa la que se marca. Solo cambia lo que se pinta, no lo que se permite.
 */
export function rutaParaMenu(pathname: string, search: string): string {
  if (!pathname.startsWith(BASE_FICHAS)) return pathname;
  const crudo = new URLSearchParams(search).get("desde");
  const desde = crudo ? (SECCIONES_RENOMBRADAS[crudo] ?? crudo) : null;
  return desde && SECCION_VALIDA.test(desde) ? PREFIJO_PORTAL_BANCOS + desde : pathname;
}

/**
 * ¿La solicitud se abre en la vista completa del broker en vez del panel lateral?
 * Solo las de su canal y con cuenta de cobranza (la vista cuelga de ella), para
 * quien ve el submenú Broker sin ser usuario de un banco. El banco sigue con su panel.
 */
export function abreEnVistaBroker(
  lead: { canal?: string | null; idCuentaCobranza?: number | null },
  quien: { puedeVerBroker: boolean; idBanco: number | null | undefined },
): boolean {
  return lead.canal === "broker" && lead.idCuentaCobranza != null && quien.puedeVerBroker && quien.idBanco == null;
}

/** Permiso del catálogo que habilita derivar una solicitud del broker a un banco. */
export const PERMISO_DERIVAR_A_BANCO = "derivar_a_banco";

/**
 * ¿Quién deriva? Lo que exige la RPC `broker_derivar_solicitud`: no ser usuario de un banco y
 * tener `derivar_a_banco` en el submenú del broker (el Super Admin lo tiene siempre). Mientras el
 * ambiente no tenga ese permiso en el catálogo, la RPC sigue pidiendo `actualizar` y aquí también.
 */
export function puedeDerivarBroker(quien: {
  idBanco: number | null | undefined;
  puedeDerivar: boolean;
  puedeActualizar: boolean;
  /** `derivar_a_banco` ya existe en `permisos`. null = aún no se sabe (se trata como que no). */
  permisoDerivarExiste: boolean | null;
}): boolean {
  if (quien.idBanco != null) return false;
  return quien.permisoDerivarExiste === true ? quien.puedeDerivar : quien.puedeActualizar;
}

/**
 * Bloque Banco de una solicitud: se ve en las del canal broker, del lado del broker (el banco
 * que la recibe ya es dueño). Se puede derivar abierta sin banco o cerrada sin crédito
 * (rechazada, desistida), la misma regla de la RPC.
 */
export function estadoDerivar(
  lead: { canal?: string | null; bankId?: string | null; status: string },
  quien: { idBanco: number | null | undefined; puedeDerivar: boolean },
): { visible: boolean; habilitado: boolean; rederivable: boolean } {
  const visible = lead.canal === "broker" && quien.idBanco == null;
  const cerrada = STATUS_DESCRIPTORS[lead.status as LeadStatus]?.isTerminal ?? false;
  const rederivable = lead.status === "rechazado" || lead.status === "desistido";
  const habilitado = visible && quien.puedeDerivar && ((!lead.bankId && !cerrada) || rederivable);
  return { visible, habilitado, rederivable };
}

// ─── Equipo del broker: operador y supervisor (ticket #1245, fase 3) ──────────

/**
 * Nombres de los roles del broker en `roles` (los ids cambian entre ambientes).
 * "Broker Bancario" es el OPERADOR: lleva los casos. "Supervisor Broker" hace lo
 * mismo y además administra al equipo.
 */
export const ROL_BROKER_OPERADOR = "Broker Bancario";
export const ROL_BROKER_SUPERVISOR = "Supervisor Broker";

/** Nivel dentro del broker. */
export type NivelBroker = "operador" | "supervisor";

/** Nivel del broker de un rol por su nombre, sin importar mayúsculas. null = no es del broker. */
export function nivelRolBroker(nombre: string | null | undefined): NivelBroker | null {
  const n = (nombre ?? "").trim().toLowerCase();
  if (n === ROL_BROKER_OPERADOR.toLowerCase()) return "operador";
  if (n === ROL_BROKER_SUPERVISOR.toLowerCase()) return "supervisor";
  return null;
}

/** ¿Es uno de los dos roles del broker (operador o supervisor)? */
export function esRolBroker(nombre: string | null | undefined): boolean {
  return nivelRolBroker(nombre) != null;
}

/** Ids de los dos roles del broker; null = el rol no existe (o está inactivo) en el ambiente. */
export interface RolesBroker {
  operadorRolId: number | null;
  supervisorRolId: number | null;
}

/** Ids de los roles del broker dentro de las filas de `roles`. */
export function resolverRolesBroker(roles: { id: number; nombre: string | null }[]): RolesBroker {
  return {
    operadorRolId: roles.find((r) => nivelRolBroker(r.nombre) === "operador")?.id ?? null,
    supervisorRolId: roles.find((r) => nivelRolBroker(r.nombre) === "supervisor")?.id ?? null,
  };
}

/** Ids que existen, para filtrar `usuarios.rol_id` por los dos roles. */
export function idsRolesBroker(roles: RolesBroker | null | undefined): number[] {
  return [roles?.operadorRolId, roles?.supervisorRolId].filter((v): v is number => v != null);
}

/** Nivel de un usuario del broker por su `rol_id`. null = no es de ninguno de los dos roles. */
export function nivelPorRolId(rolId: number | null | undefined, roles: RolesBroker | null | undefined): NivelBroker | null {
  if (rolId == null || !roles) return null;
  if (rolId === roles.supervisorRolId) return "supervisor";
  if (rolId === roles.operadorRolId) return "operador";
  return null;
}

/** Quién quiere administrar al equipo del broker. */
export interface ActorEquipoBroker {
  /** Super Administrador o quien puede "Ver como" (SOZU). */
  esSozu: boolean;
  /** Nivel de su rol en el broker; null = otro rol. */
  nivel: NivelBroker | null;
  /** El rol Supervisor Broker ya existe en este ambiente. */
  hayRolSupervisor: boolean;
}

/**
 * ¿Puede administrar (editar, desactivar, reenviar, resetear) a un miembro de ese
 * nivel? Solo cuenta el ROL: el permiso de la vista (`submenus_permisos`) se exige
 * aparte y la BD tiene la última palabra.
 *   - SOZU administra a todos, también a los supervisores.
 *   - A un supervisor solo lo administra SOZU.
 *   - El operador no administra a nadie, salvo que el rol Supervisor Broker aún no
 *     exista (front adelante de la migración): entonces sigue como hasta hoy.
 *   - Cualquier otro rol con permiso en la vista (el supervisor incluido): a los operadores.
 */
export function puedeGestionarMiembroBroker(actor: ActorEquipoBroker, objetivo: NivelBroker): boolean {
  if (actor.esSozu) return true;
  if (objetivo === "supervisor") return false;
  if (actor.nivel === "operador") return !actor.hayRolSupervisor;
  return true;
}

/** Qué puede hacer quien edita con la fila de un miembro del equipo del broker. */
export interface AccionesMiembroBroker {
  /** Editar nombre y teléfono, reenviar confirmación, resetear, desactivar o reactivar. */
  administrar: boolean;
  /** Cambiar su correo de acceso (siempre por la edge `update-user-email`). */
  cambiarCorreo: boolean;
}

/**
 * Acciones sobre la fila de un miembro: el permiso de la vista (`actualizar`,
 * `cambiar_correo`) Y el nivel. Así el supervisor cambia el correo de sus
 * operadores pero no el de otro supervisor (eso es de SOZU), y el operador no
 * toca nada. Sin `actor` (roles aún cargando) no se ofrece nada.
 */
export function accionesMiembroBroker(
  actor: ActorEquipoBroker | null,
  objetivo: NivelBroker,
  permisos: { actualizar: boolean; cambiarCorreo: boolean },
): AccionesMiembroBroker {
  const administrar = !!actor && permisos.actualizar && puedeGestionarMiembroBroker(actor, objetivo);
  return { administrar, cambiarCorreo: administrar && permisos.cambiarCorreo };
}

/**
 * Niveles que puede dar de alta (vacío = ninguno). Un supervisor solo crea
 * operadores; los supervisores los crea SOZU.
 */
export function nivelesAltaBroker(actor: ActorEquipoBroker): NivelBroker[] {
  if (!puedeGestionarMiembroBroker(actor, "operador")) return [];
  return actor.esSozu && actor.hayRolSupervisor ? ["operador", "supervisor"] : ["operador"];
}

/**
 * Rol cuyas vistas se pintan al "Ver como Broker bancario": el del supervisor, que
 * tiene todo lo del operador y además el equipo. Sin el rol supervisor, el del operador.
 */
export function rolMenuVerComoBroker(roles: RolesBroker | null | undefined): number | null {
  return roles?.supervisorRolId ?? roles?.operadorRolId ?? null;
}

// ─── Dueño de la unidad y orden de Clientes por contactar (ticket #1245) ─────
// El dueño es la entidad con la que se dio de alta la unidad (Tallwood, Corporativo
// Jmdq, …): `propiedades.id_entidad_relacionada_dueno` → persona, nombre comercial o
// legal. Lo trae la RPC desde la migración 20261008163417; antes no llega.

/** Texto de una unidad sin dueño (y opción del filtro "Dueño"). */
export const ETIQUETA_SIN_DUENO = "Sin dueño";
/** Valor del filtro "Dueño" para las unidades sin dueño (un nombre real nunca es este). */
export const FILTRO_SIN_DUENO = "__sin_dueno__";

/**
 * Nombre del dueño para comparar: sin acentos, en minúsculas y solo letras y números
 * ("TALLWOOD S.A." → "tallwoodsa", "J.M.D.Q." → "jmdq").
 */
export function claveDueno(nombre: string | null | undefined): string {
  return (nombre ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

/**
 * Prioridad del dueño en Clientes por contactar (pedido de Tomás: la broker trabaja
 * primero lo de Tallwood): 0 = Tallwood, 2 = Corporativo Jmdq, 1 = cualquier otro o
 * sin dueño. Misma regla que el ORDER BY de la RPC.
 */
export function prioridadDueno(nombre: string | null | undefined): 0 | 1 | 2 {
  const clave = claveDueno(nombre);
  if (clave.includes("tallwood")) return 0;
  if (clave.includes("jmdq")) return 2;
  return 1;
}

/**
 * Número de la unidad para el orden natural: el primer número que trae
 * ("1001" → 1001, "U-713" → 713, "PH-1" → 1, "Oficina 2" → 2). null = sin número.
 */
export function numeroUnidad(unidad: string | null | undefined): number | null {
  const m = /\d+/.exec(unidad ?? "");
  return m ? Number(m[0]) : null;
}

/** Orden natural de unidades: por su número (101 < 102 < 1001), sin número al final, luego el texto. */
export function compararUnidades(a: string | null | undefined, b: string | null | undefined): number {
  const na = numeroUnidad(a);
  const nb = numeroUnidad(b);
  if (na !== nb) {
    if (na == null) return 1;
    if (nb == null) return -1;
    return na - nb;
  }
  const ta = a ?? "";
  const tb = b ?? "";
  return ta < tb ? -1 : ta > tb ? 1 : 0;
}

/** Lo que se necesita de una fila para ordenarla. */
interface FilaOrdenBroker {
  id_cuenta_cobranza: number;
  proyecto: string;
  unidad: string;
  /** Ausente sin la migración 20261008163417: cuenta como sin dueño. */
  dueno?: string | null;
}

/**
 * Orden por defecto de Clientes por contactar (el mismo que la RPC, para que valga
 * aunque el ambiente no tenga la migración): Tallwood primero, los demás y sin dueño
 * en medio, Corporativo Jmdq al final; dentro, por desarrollo y por unidad en orden
 * natural. Devuelve un arreglo nuevo.
 */
export function ordenarClientesBroker<T extends FilaOrdenBroker>(filas: readonly T[]): T[] {
  return [...filas].sort(
    (a, b) =>
      prioridadDueno(a.dueno) - prioridadDueno(b.dueno) ||
      a.proyecto.localeCompare(b.proyecto, "es") ||
      compararUnidades(a.unidad, b.unidad) ||
      a.id_cuenta_cobranza - b.id_cuenta_cobranza,
  );
}

/** Nombre a mostrar del dueño (vacío o ausente = sin dueño). */
export function nombreDueno(dueno: string | null | undefined): string | null {
  const n = (dueno ?? "").trim();
  return n || null;
}

/** ¿La fila pasa el filtro "Dueño"? "" = todos; `FILTRO_SIN_DUENO` = las que no tienen. */
export function coincideDueno(dueno: string | null | undefined, filtro: string): boolean {
  if (!filtro) return true;
  const n = nombreDueno(dueno);
  return filtro === FILTRO_SIN_DUENO ? n == null : n === filtro;
}

/** Opción del filtro "Dueño". */
export interface OpcionDueno {
  value: string;
  label: string;
  cuantos: number;
}

/**
 * Opciones del filtro "Dueño": cada dueño presente con cuántos clientes tiene, en el
 * orden de la lista (Tallwood primero, Corporativo Jmdq al final, los demás por
 * nombre) y "Sin dueño" al final si hay. Sin "Todos" (lo pone la pantalla).
 */
export function opcionesFiltroDueno(filas: readonly { dueno?: string | null }[]): OpcionDueno[] {
  const cuantos = new Map<string, number>();
  let sinDueno = 0;
  for (const f of filas) {
    const n = nombreDueno(f.dueno);
    if (n == null) sinDueno++;
    else cuantos.set(n, (cuantos.get(n) ?? 0) + 1);
  }
  const opciones = [...cuantos.entries()]
    .sort(([a], [b]) => prioridadDueno(a) - prioridadDueno(b) || a.localeCompare(b, "es"))
    .map(([n, c]) => ({ value: n, label: n, cuantos: c }));
  if (sinDueno > 0) opciones.push({ value: FILTRO_SIN_DUENO, label: ETIQUETA_SIN_DUENO, cuantos: sinDueno });
  return opciones;
}

// ─── Derivar a quien eligió crédito y aún no manda su solicitud ──────────────

/**
 * Cómo autorizó el cliente compartir sus datos con el banco cuando la solicitud la arma el broker
 * por él (en la app lo acepta él mismo). Queda en la Bitácora de la solicitud.
 */
export const MEDIOS_CONSENTIMIENTO = [
  { value: "llamada", label: "Llamada" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "correo", label: "Correo" },
  { value: "presencial", label: "En persona" },
] as const;
export type MedioConsentimiento = (typeof MEDIOS_CONSENTIMIENTO)[number]["value"];

/** Plazos que ofrece la app al cliente (`CatalogoBancos.plazosBase`); 20 es el propuesto. */
export const PLAZOS_SOLICITUD_ANIOS = [10, 15, 20] as const;
export const PLAZO_PROPUESTO_ANIOS = 20;

export interface DatosDerivarSinSolicitud {
  idBanco: number | null;
  /** Sin banco todavía (ticket #1415): la solicitud queda en Contactado y se deriva después. */
  sinBanco?: boolean;
  monto: number;
  /** Lo que le falta por liquidar: tope del monto a financiar. */
  saldo: number;
  plazoAnios: number;
  consentimiento: boolean;
  medio: string | null;
}

/**
 * Qué falta para crear la solicitud y derivarla, o null si está completa. Misma regla que la RPC
 * `broker_crear_solicitud_y_derivar`: sin la autorización del cliente no se comparte nada.
 */
export function faltanteDerivarSinSolicitud(d: DatosDerivarSinSolicitud): string | null {
  if (d.idBanco == null && !d.sinBanco) return "Elige el banco o \"Sin banco todavía\".";
  if (!Number.isFinite(d.monto) || d.monto <= 0) return "Captura el monto a financiar.";
  // Centavos enteros: comparar floats con tolerancia deja pasar montos de más.
  if (Math.round(d.monto * 100) > Math.round(d.saldo * 100)) return "El monto no puede pasar de lo que le falta por liquidar.";
  if (!(PLAZOS_SOLICITUD_ANIOS as readonly number[]).includes(d.plazoAnios)) return "Elige el plazo.";
  if (!d.consentimiento) return "Confirma que el cliente autorizó compartir sus datos con el banco.";
  if (!MEDIOS_CONSENTIMIENTO.some((m) => m.value === d.medio)) return "Indica cómo te dio su autorización.";
  return null;
}
